<template>
  <div>
    <!-- Social Graphic Settings Section -->
    <div class="settings-section">
      <h4 class="section-title">
        Social Graphic Settings
      </h4>

      <div class="tb-form-group">
        <label
          class="tb-label"
          for="displaySelector"
        >
          Social Graphic
        </label>

        <select
          id="displaySelector"
          v-model="selectedDisplayId"
          class="tb-input"
          data-entity="producer-editor-socialDisplay"
          data-test="graphics-editor-socialDisplay"
        >
          <option
            v-for="display in displays"
            :key="display._id"
            :value="display._id"
          >
            {{ display.title }}
          </option>
        </select>
      </div>

      <div
        v-if="isMultiPostDisplay"
        class="tb-form-group"
      >
        <p class="text-producer-slate-textMuted text-[13px]">
          <span class="fas fa-info-circle mr-2" />
          Multi-post displays require a full board. Individual post toggling is not available.
        </p>
      </div>

      <div class="tb-form-group">
        <label class="tb-label">
          <input
            v-model="interactive"
            type="checkbox"
            data-entity="producer-editor-interactive"
            data-test="graphics-editor-interactive"
          >
          Interactive
        </label>
      </div>

      <div class="tb-form-group">
        <label class="tb-label">
          <input
            v-model="showCta"
            type="checkbox"
            data-entity="producer-editor-showCta"
            data-test="graphics-editor-showCta"
          >
          Show CTA
        </label>
      </div>

      <div
        v-if="showCta"
        class="tb-form-group"
      >
        <label
          class="tb-label"
          for="ctaInput"
        >
          CTA Text
        </label>

        <input
          id="ctaInput"
          v-model="cta"
          type="text"
          class="tb-input"
          data-entity="producer-editor-cta"
          data-test="graphics-editor-cta"
          @keyup.right.stop
          @keyup.left.stop
        >
      </div>

      <div class="tb-form-group">
        <label
          class="tb-label"
          for="postAnimationInput"
        >
          Post Animation
        </label>

        <select
          id="postAnimationInput"
          v-model="postAnimation"
          class="tb-input"
          data-entity="producer-editor-postAnimation"
          data-test="graphics-editor-postAnimation"
        >
          <option
            v-for="animation in availableAnimations"
            :key="animation.value"
            :value="animation.value"
          >
            {{ animation.label }}
          </option>
        </select>
      </div>

      <div class="tb-form-group">
        <label
          class="tb-label"
          for="cycleSpeedInput"
        >
          Cycle Speed (1 - 100 seconds)
        </label>

        <input
          id="cycleSpeedInput"
          v-model.number.lazy="cycleSpeed"
          type="number"
          class="tb-input"
          min="1"
          max="100"
          data-entity="producer-editor-cycleSpeed"
          data-test="graphics-editor-cycleSpeed"
          @keyup.right.stop
          @keyup.left.stop
        >
      </div>

      <div class="tb-form-group">
        <label class="tb-label">
          Background
        </label>

        <div
          class="colorpicker"
          data-entity="producer-editor-toggleBackgroundColorPicker"
          data-test="graphics-editor-background-color"
        >
          <colorpicker
            :color="background"
            :large="true"
            show-buttons
            data-test="graphics-editor-background-color"
            @change="updateField('background', $event)"
          />
        </div>
      </div>

      <!-- QR Code -->
      <div class="tb-form-group">
        <label class="tb-label">
          <input
            v-model="showQrCode"
            type="checkbox"
            data-entity="producer-editor-showQrCode"
            data-test="graphics-editor-showQrCode"
          >
          Show QR Code
        </label>
      </div>

      <template v-if="showQrCode">
        <!-- eslint-disable vue/no-v-model-argument -->
        <qr-code-url
          v-model:url="permalink"
          v-model:short-url="shortLink"
          :item="graphic"
          :element="element"
          :entity-id="element.id"
        />
        <!-- eslint-enable vue/no-v-model-argument -->
      </template>
    </div>

    <!-- Theme Change Warning Modal -->
    <modal
      v-if="showThemeChangeModal"
      @close="cancelThemeChange"
    >
      <template #header>
        <div class="title">Clear Posts?</div>
      </template>

      <p>
        The Social Graphic you've selected
        ({{ pendingDisplay?.title }}) requires a full Board, and does not support individual posts.
      </p>
      <p>
        Switching to this Social Graphic will clear all
        individual social posts. Do you want to continue?
      </p>

      <template #footer>
        <button
          class="tb-btn tb-btn-text"
          @click="cancelThemeChange"
        >
          Cancel
        </button>
        <button
          class="tb-btn tb-btn-contained warning"
          @click="confirmThemeChange"
        >
          Clear Posts &amp; Switch
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import catchify from 'catchify';
import { getThemes, getTheme } from '../../../../services/themes';
import Shortener from '../../../../services/shortener';
import Modal from '../../../modal/Modal.vue';
import QrCodeUrl from '../../../shared/QrCodeUrl.vue';

export default {
  components: {
    Modal,
    QrCodeUrl,
  },

  props: {
    element: {
      type: Object,
      required: true,
    },

    graphic_fields: {
      type: Object,
      required: true,
    },

    graphic: {
      type: Object,
      required: true,
    },
  },

  watch: {
    currentDisplay: {
      immediate: true,
      handler(newDisplay, oldDisplay) {
        // Initialize defaults from theme when display is first loaded or changed
        if (newDisplay && newDisplay !== oldDisplay) {
          this.checkThemeChangeAndClearPosts(newDisplay, oldDisplay);
          this.initializeDefaultsFromTheme(newDisplay);
        }
      },
    },

    // Watch for QR code being enabled with existing permalink but no short URL
    async showQrCode(enabled) {
      if (enabled && this.permalink && !this.shortLink) {
        // QR code was just enabled and we have a permalink but no short URL
        // Generate short URL directly
        await this.generateShortUrl(this.permalink);
      }
    },
  },

  data() {
    return {
      displays: [],
      showThemeChangeModal: false,
      pendingDisplay: null,
      // Track which fields have been explicitly customized by the user
      customizedFields: new Set(),
      animationsByLayout: {
        isolated: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Bounce', label: 'Bounce' },
          { value: 'Roll', label: 'Roll' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Swing', label: 'Swing' },
          { value: 'Fly', label: 'Fly' },
        ],
        cinematic: [
          { value: 'Fade', label: 'Fade' },
          { value: 'Ken Burns', label: 'Ken Burns' },
          { value: 'Zoom In', label: 'Zoom In' },
          { value: 'Zoom Out', label: 'Zoom Out' },
        ],
        lowerthird: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Bounce', label: 'Bounce' },
          { value: 'Roll', label: 'Roll' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Swing', label: 'Swing' },
          { value: 'Fly', label: 'Fly' },
        ],
        lowerthirdscroll: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Bounce', label: 'Bounce' },
          { value: 'Roll', label: 'Roll' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Swing', label: 'Swing' },
          { value: 'Fly', label: 'Fly' },
        ],
        ticker: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
        ],
        upnext: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Slide Down', label: 'Slide Down' },
          { value: 'Fade', label: 'Fade' },
        ],
        waterfall: [
          { value: 'Fade', label: 'Fade' },
          { value: 'Slide', label: 'Slide' },
          { value: 'Slide Down', label: 'Slide Down' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Scale', label: 'Scale' },
          { value: 'Parallax', label: 'Parallax' },
        ],
        grid: [
          { value: 'Fade', label: 'Fade' },
          { value: 'Slide', label: 'Slide' },
          { value: 'Slide Down', label: 'Slide Down' },
          { value: 'Flip', label: 'Flip' },
        ],
        carousel: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Scale', label: 'Scale' },
          { value: 'Flow', label: '3D' },
        ],
        mural: [
          { value: 'Fade', label: 'Fade' },
        ],
        chat: [
          { value: 'Slide', label: 'Slide' },
        ],
        stories: [
          { value: 'Fade', label: 'Fade' },
        ],
      },
    };
  },

  computed: {
    timelineId() {
      return this.graphic.timeline_id;
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },

    currentDisplay() {
      return this.displays.find((d) => d._id === this.selectedDisplayId);
    },

    currentLayout() {
      return this.currentDisplay?.layout || this.elementFields?.layout;
    },

    availableAnimations() {
      const layout = this.currentLayout;
      return this.animationsByLayout[layout] || [{ value: 'Fade', label: 'Fade' }];
    },

    isMultiPostDisplay() {
      const layout = this.currentDisplay?.layout;
      return ['grid', 'waterfall', 'carousel', 'mural'].includes(layout);
    },

    hasIndividualPosts() {
      // Check if element has individual posts (not a tagboard)
      const posts = this.elementFields?.posts || this.element.posts;
      if (!posts || posts.length === 0) {
        return false;
      }

      // If first post is a tagboard type, return false
      return posts[0]?.type !== 'tagboard';
    },

    selectedDisplayId: {
      get() {
        return this.elementFields?.displayId || this.element.displayId;
      },

      set(value) {
        const display = this.displays.find((d) => d._id === value);
        if (!display) {
          return;
        }

        // Check if switching to a multi-post display with individual posts present
        const newIsMultiPost = ['grid', 'waterfall', 'carousel', 'mural'].includes(display.layout);
        if (newIsMultiPost && this.hasIndividualPosts) {
          // Show warning modal
          this.pendingDisplay = display;
          this.showThemeChangeModal = true;
          return;
        }

        // Safe to update
        this.updateDisplayUrl(display);
      },
    },

    interactive: {
      get() {
        const hasValue = this.elementFields?.interactive !== undefined;
        if (hasValue) {
          return this.elementFields.interactive;
        }

        return this.currentDisplay?.settings?.interactive ?? true;
      },

      set(value) {
        // Mark as customized when user changes it
        this.customizedFields.add('interactive');
        this.updateField('interactive', value);
      },
    },

    showCta: {
      get() {
        const hasValue = this.elementFields?.showCta !== undefined;
        if (hasValue) {
          return this.elementFields.showCta;
        }

        return this.currentDisplay?.settings?.showCtaText ?? true;
      },

      set(value) {
        this.customizedFields.add('showCta');
        this.updateField('showCta', value);
      },
    },

    cta: {
      get() {
        const hasValue = this.elementFields?.cta !== undefined && this.elementFields.cta !== '';
        if (hasValue) {
          return this.elementFields.cta;
        }

        return this.currentDisplay?.settings?.ctaText ?? '';
      },

      set(value) {
        this.customizedFields.add('cta');
        this.updateField('cta', value);
      },
    },

    /**
     * Check for any postAnimation overrides in order:
     * 1. fields set via producer
     * 2. property set in graphic editor
     * 3. animation on the theme itself
     * 4. hardcoded fallback (really shouldn't happen, but alas)
     */
    postAnimation: {
      get() {
        return (
          this.elementFields?.postAnimation // Fields override
          || this.element.postAnimation // Graphic override
          || this.currentDisplay?.settings?.animation // Theme default
          || this.availableAnimations[0]?.value // Fallback to first valid animation
          || 'Fade' // Final fallback jic the theme is completely invalid
        );
      },

      set(value) {
        this.customizedFields.add('postAnimation');
        this.updateField('postAnimation', value);
      },
    },

    cycleSpeed: {
      get() {
        const hasValue = this.elementFields?.cycleSpeed !== undefined;
        if (hasValue) {
          const {cycleSpeed} = this.elementFields;
          return Math.floor(cycleSpeed / 1e3);
        }

        // Get default from theme settings
        const themeDisplayTime = this.currentDisplay?.settings?.displayTime;
        if (themeDisplayTime) {
          return Math.floor(themeDisplayTime / 1e3);
        }

        return 5;
      },

      set(value) {
        this.customizedFields.add('cycleSpeed');
        this.updateField(
          'cycleSpeed',
          Math.min(Math.max(value || 1, 1), 100) * 1e3,
        );
      },
    },

    background: {
      get() {
        const hasValue = this.elementFields?.background !== undefined;
        if (hasValue) {
          return this.elementFields.background;
        }

        return this.currentDisplay?.settings?.backgroundColor ?? 'transparent';
      },

      set(value) {
        this.customizedFields.add('background');
        this.updateField('background', value);
      },
    },

    showQrCode: {
      get() {
        const hasValue = this.elementFields?.showQrCode !== undefined;
        if (hasValue) {
          return this.elementFields.showQrCode;
        }

        return this.currentDisplay?.settings?.showQrCode ?? false;
      },

      set(value) {
        this.customizedFields.add('showQrCode');
        this.updateField('showQrCode', value);
      },
    },

    permalink: {
      get() {
        const hasValue = this.elementFields?.permalink !== undefined && this.elementFields.permalink !== '';
        if (hasValue) {
          return this.elementFields.permalink;
        }

        return this.currentDisplay?.settings?.permalink ?? '';
      },

      set(value) {
        this.customizedFields.add('permalink');
        this.updateField('permalink', value);
      },
    },

    shortLink: {
      get() {
        return this.elementFields?.shortLink ?? '';
      },

      set(value) {
        this.updateField('shortLink', value);
      },
    },

    ...mapState({
      production(state) {
        return state.production.production;
      },
    }),
  },

  async mounted() {
    await this.loadDisplays();

    // Ensure the currently selected theme is loaded
    await this.ensureSelectedThemeLoaded();

    // Initialize defaults after displays are loaded
    if (this.currentDisplay) {
      this.initializeDefaultsFromTheme(this.currentDisplay);
    }

    // Generate short URL if QR code is enabled but no short URL exists
    if (this.showQrCode && this.permalink && !this.shortLink) {
      await this.generateShortUrl(this.permalink);
    }
  },

  methods: {
    async generateShortUrl(url) {
      if (!url) {
        return;
      }

      const [err, resp] = await catchify(
        Shortener.getShortURL(url, {
          user: this.production.owner,
          production: {
            id: this.production.id,
            title: this.production.title,
          },
          item: this.graphic,
          element: this.element,
        }),
      );

      if (err) {
        console.error('Failed to generate short URL for social display:', err);
        return;
      }

      const { shortUrl } = resp.data;
      this.shortLink = shortUrl;
    },

    async loadDisplays() {
      try {
        const data = await getThemes({ limit: 100 });
        this.displays = data.themes || [];
      } catch (error) {
        console.error('Error loading displays:', error);
      }
    },

    async ensureSelectedThemeLoaded() {
      const selectedId = this.selectedDisplayId;
      if (!selectedId) {
        return;
      }

      // Check if the selected theme is already in the loaded displays
      const isLoaded = this.displays.some((d) => d._id === selectedId);
      if (isLoaded) {
        return;
      }

      // Fetch the selected theme by ID
      try {
        const theme = await getTheme(selectedId);
        if (theme) {
          // Add the theme to the displays list
          this.displays.push(theme);
        }
      } catch (error) {
        console.error('Error loading selected theme:', error);
      }
    },

    checkThemeChangeAndClearPosts(newDisplay, oldDisplay) {
      // If already handled by selectedDisplayId setter, skip
      if (this.showThemeChangeModal) {
        return;
      }

      // Check if the new display requires clearing posts
      const newIsMultiPost = newDisplay && ['grid', 'waterfall', 'carousel', 'mural'].includes(newDisplay.layout);
      const oldIsMultiPost = oldDisplay && ['grid', 'waterfall', 'carousel', 'mural'].includes(oldDisplay.layout);

      // If switching from single-post to multi-post display and has individual posts, show warning
      if (!oldIsMultiPost && newIsMultiPost && this.hasIndividualPosts) {
        this.pendingDisplay = newDisplay;
        this.showThemeChangeModal = true;
      }
    },

    confirmThemeChange() {
      if (!this.pendingDisplay) {
        return;
      }

      // Clear all posts
      this.updateField('posts', []);

      // Update to the new display (with theme change flag)
      this.updateField('displayId', this.pendingDisplay._id);
      this.updateField('layout', this.pendingDisplay.layout);
      this.initializeDefaultsFromTheme(this.pendingDisplay, true);

      // Close modal and clear pending
      this.showThemeChangeModal = false;
      this.pendingDisplay = null;
    },

    cancelThemeChange() {
      // Just close the modal without making changes
      this.showThemeChangeModal = false;
      this.pendingDisplay = null;
    },

    initializeDefaultsFromTheme(display, isThemeChange = false) {
      if (!display?.settings) {
        return;
      }

      // Get the previous display for comparison (when changing themes)
      const oldDisplay = isThemeChange ? this.displays.find(d => d._id === this.elementFields?.displayId) : null;

      // Only initialize fields that haven't been set yet or match the old theme's defaults
      const fieldsToInit = [];

      // Interactive
      if (this.elementFields?.interactive === undefined) {
        // Check if element has a value set from graphics editor first
        if (this.element.interactive !== undefined) {
          fieldsToInit.push({ field: 'interactive', value: this.element.interactive });
        } else if (display.settings.interactive !== undefined) {
          // Fall back to theme default
          fieldsToInit.push({ field: 'interactive', value: display.settings.interactive });
        }
      } else if (isThemeChange && !this.customizedFields.has('interactive')) {
        // Theme change and field not customized - check if it matches old theme default
        const matchesOldDefault = oldDisplay?.settings?.interactive === this.elementFields.interactive;
        if (matchesOldDefault && display.settings.interactive !== undefined) {
          fieldsToInit.push({ field: 'interactive', value: display.settings.interactive });
        }
      }

      // Show CTA
      if (this.elementFields?.showCta === undefined) {
        // Check if element has a value set from graphics editor first
        if (this.element.showCta !== undefined) {
          fieldsToInit.push({ field: 'showCta', value: this.element.showCta });
        } else if (display.settings.showCtaText !== undefined) {
          // Fall back to theme default
          fieldsToInit.push({ field: 'showCta', value: display.settings.showCtaText });
        }
      } else if (isThemeChange && !this.customizedFields.has('showCta')) {
        const matchesOldDefault = oldDisplay?.settings?.showCtaText === this.elementFields.showCta;
        if (matchesOldDefault && display.settings.showCtaText !== undefined) {
          fieldsToInit.push({ field: 'showCta', value: display.settings.showCtaText });
        }
      }

      // CTA Text
      if (!this.elementFields?.cta || this.elementFields.cta === '') {
        // Check if element has a value set from graphics editor first
        if (this.element.cta) {
          fieldsToInit.push({ field: 'cta', value: this.element.cta });
        } else if (display.settings.ctaText) {
          // Fall back to theme default
          fieldsToInit.push({ field: 'cta', value: display.settings.ctaText });
        }
      } else if (isThemeChange && !this.customizedFields.has('cta')) {
        const matchesOldDefault = oldDisplay?.settings?.ctaText === this.elementFields.cta;
        if (matchesOldDefault && display.settings.ctaText) {
          fieldsToInit.push({ field: 'cta', value: display.settings.ctaText });
        }
      }

      // Post Animation
      if (this.elementFields?.postAnimation === undefined) {
        // Check if element has a value set from graphics editor first
        if (this.element.postAnimation !== undefined) {
          fieldsToInit.push({ field: 'postAnimation', value: this.element.postAnimation });
        } else if (display.settings.animation) {
          // Fall back to theme default
          fieldsToInit.push({ field: 'postAnimation', value: display.settings.animation });
        }
      } else if (isThemeChange) {
        // Check if current animation is valid for the new layout
        const currentAnimation = this.elementFields.postAnimation;
        const newLayoutAnimations = this.animationsByLayout[display.layout] || [];
        const isValidForNewLayout = newLayoutAnimations.some(anim => anim.value === currentAnimation);

        if (!isValidForNewLayout) {
          // Current animation is not valid for new layout - force update
          const newAnimation = display.settings.animation || newLayoutAnimations[0]?.value || 'Fade';
          fieldsToInit.push({ field: 'postAnimation', value: newAnimation });
        } else if (!this.customizedFields.has('postAnimation')) {
          // Animation is valid, check if it matches old theme default
          const matchesOldDefault = oldDisplay?.settings?.animation === currentAnimation;
          if (matchesOldDefault && display.settings.animation) {
            fieldsToInit.push({ field: 'postAnimation', value: display.settings.animation });
          }
        }
      }

      // Cycle Speed
      if (this.elementFields?.cycleSpeed === undefined) {
        // Check if element has a value set from graphics editor first
        if (this.element.cycleSpeed !== undefined) {
          fieldsToInit.push({ field: 'cycleSpeed', value: this.element.cycleSpeed });
        } else if (display.settings.displayTime) {
          // Fall back to theme default
          fieldsToInit.push({ field: 'cycleSpeed', value: display.settings.displayTime });
        }
      } else if (isThemeChange && !this.customizedFields.has('cycleSpeed')) {
        const matchesOldDefault = oldDisplay?.settings?.displayTime === this.elementFields.cycleSpeed;
        if (matchesOldDefault && display.settings.displayTime) {
          fieldsToInit.push({ field: 'cycleSpeed', value: display.settings.displayTime });
        }
      }

      // Background
      if (this.elementFields?.background === undefined) {
        // Check if element has a value set from graphics editor first
        if (this.element.background !== undefined) {
          fieldsToInit.push({ field: 'background', value: this.element.background });
        } else if (display.settings.backgroundColor) {
          // Fall back to theme default
          fieldsToInit.push({ field: 'background', value: display.settings.backgroundColor });
        }
      } else if (isThemeChange && !this.customizedFields.has('background')) {
        const matchesOldDefault = oldDisplay?.settings?.backgroundColor === this.elementFields.background;
        if (matchesOldDefault && display.settings.backgroundColor) {
          fieldsToInit.push({ field: 'background', value: display.settings.backgroundColor });
        }
      }

      // Show QR Code
      if (this.elementFields?.showQrCode === undefined) {
        if (this.element.showQrCode !== undefined) {
          fieldsToInit.push({ field: 'showQrCode', value: this.element.showQrCode });
        } else if (display.settings.showQrCode !== undefined) {
          fieldsToInit.push({ field: 'showQrCode', value: display.settings.showQrCode });
        }
      } else if (isThemeChange && !this.customizedFields.has('showQrCode')) {
        const matchesOldDefault = oldDisplay?.settings?.showQrCode === this.elementFields.showQrCode;
        if (matchesOldDefault && display.settings.showQrCode !== undefined) {
          fieldsToInit.push({ field: 'showQrCode', value: display.settings.showQrCode });
        }
      }

      // Permalink
      if (!this.elementFields?.permalink || this.elementFields.permalink === '') {
        if (this.element.permalink) {
          fieldsToInit.push({ field: 'permalink', value: this.element.permalink });
        } else if (display.settings.permalink) {
          fieldsToInit.push({ field: 'permalink', value: display.settings.permalink });
        }
      } else if (isThemeChange && !this.customizedFields.has('permalink')) {
        const matchesOldDefault = oldDisplay?.settings?.permalink === this.elementFields.permalink;
        if (matchesOldDefault && display.settings.permalink) {
          fieldsToInit.push({ field: 'permalink', value: display.settings.permalink });
        }
      }

      // Batch emit all default values
      fieldsToInit.forEach(({ field, value }) => {
        this.updateField(field, value);
      });
    },

    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    updateDisplayUrl(display) {
      this.updateField('displayId', display._id);
      this.updateField('layout', display.layout);

      // Initialize defaults from the newly selected theme
      // Pass true to indicate this is a theme change (not initial load)
      this.initializeDefaultsFromTheme(display, true);
    },
  },
};
</script>

<style lang="scss" scoped>
.settings-section {
  margin-bottom: 1.5rem;
}

.section-title {
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #64748b;
}
</style>
